import DOMPurify from 'dompurify';
import type {
  AppraisalDetailResponse,
  InterviewSummaryEntry,
} from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';
import { formatDate } from '@/utils/dateFormatter';

interface AppraisalPdfContentProps {
  appraisal: AppraisalDetailResponse;
  permissions: RecordAccess | undefined;
  interviewSummaryEntries: InterviewSummaryEntry[];
}

const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li'] });
};

const styles = {
  container: {
    fontFamily: '"Nunito Sans", "Segoe UI", "Helvetica Neue", "Arial", sans-serif',
    fontSize: '12px',
    lineHeight: '1.5',
    color: '#333',
    padding: '40px',
    backgroundColor: '#fff',
    width: '714px',
  } as React.CSSProperties,
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '24px',
    color: '#1a1a1a',
    borderBottom: '2px solid #00695c',
    paddingBottom: '8px',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 700,
    marginTop: '24px',
    marginBottom: '12px',
    color: '#00695c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 24px',
    marginBottom: '8px',
  } as React.CSSProperties,
  detailRow: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  detailLabel: {
    fontWeight: 600,
    minWidth: '140px',
    color: '#555',
  } as React.CSSProperties,
  detailValue: {
    color: '#1a1a1a',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '8px',
    fontSize: '11px',
  } as React.CSSProperties,
  th: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'left' as const,
    fontWeight: 600,
    fontSize: '11px',
    color: '#555',
  } as React.CSSProperties,
  thCenter: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'center' as const,
    fontWeight: 600,
    fontSize: '11px',
    color: '#555',
  } as React.CSSProperties,
  td: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,
  tdCenter: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  feedbackItem: {
    marginBottom: '12px',
    paddingLeft: '8px',
  } as React.CSSProperties,
  feedbackNumber: {
    fontWeight: 600,
    color: '#555',
    marginBottom: '4px',
  } as React.CSSProperties,
  feedbackContent: {
    lineHeight: '1.6',
  } as React.CSSProperties,
};

function isSectionVisible(permissions: RecordAccess | undefined, sectionKey: string): boolean {
  if (!permissions) return false;
  const section = permissions.sections[sectionKey];
  return section !== undefined && section.access !== 'none';
}

const RATING_LABELS: Record<string, string> = {
  BT: 'Below Track',
  OT: 'On Track',
  AT: 'Above Track',
  O: 'Outstanding',
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}:</span>
      <span style={styles.detailValue}>{value}</span>
    </div>
  );
}

export function AppraisalPdfContent({
  appraisal,
  permissions,
  interviewSummaryEntries,
}: AppraisalPdfContentProps) {
  const showInterviewSummary = isSectionVisible(permissions, 'interviewSummary');
  const showSelfAssessment = isSectionVisible(permissions, 'selfAssessment');
  const showPerformanceSummary = isSectionVisible(permissions, 'performanceSummary');
  const showSkillAreaStrengths = isSectionVisible(permissions, 'skillAreaStrengths');
  const showForwardLookingPlan = isSectionVisible(permissions, 'forwardLookingPlan');

  // Bug 2 (TG-6): use orphan-aware aggregated summary entries instead of project-tied candidates,
  // so auto-created Mentor entries and Appraisee/Collator-added entries (AppraisalJobEndId == null)
  // appear in the PDF.
  const candidates = interviewSummaryEntries;

  return (
    <div style={styles.container}>
      <div data-pdf-section style={styles.title}>Appraisal</div>

      <div data-pdf-section>
        <div style={styles.sectionTitle}>Appraisal Details</div>
        <div style={styles.detailsGrid}>
          <DetailRow label="Appraisee" value={`${appraisal.appraiseeFirstName} ${appraisal.appraiseeLastName}`} />
          <DetailRow label="Grade" value={appraisal.appraiseeGrade} />
          <DetailRow label="Office" value={appraisal.office || '-'} />
          <DetailRow label="Mentor" value={appraisal.mentorName || '-'} />
          <DetailRow label="Tenure in Grade" value={appraisal.tenureInGrade || '-'} />
          <DetailRow label="Staffing Weeks" value={String(appraisal.staffingWeeks)} />
          <DetailRow label="Last Rating" value={appraisal.lastRating ? (RATING_LABELS[appraisal.lastRating] || appraisal.lastRating) : '-'} />
          <DetailRow label="Collator" value={appraisal.collatorName || '-'} />
          <DetailRow
            label="Appraisal Period"
            value={
              appraisal.appraisalPeriodStart && appraisal.appraisalPeriodEnd
                ? `${formatDate(appraisal.appraisalPeriodStart)} \u2014 ${formatDate(appraisal.appraisalPeriodEnd)}`
                : '-'
            }
          />
          <DetailRow label="Status" value={appraisal.appraisalStatus} />
        </div>
      </div>

      {showInterviewSummary && candidates.length > 0 && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Interview Candidates</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Role</th>
                <th style={styles.thCenter}>Suggested</th>
                <th style={styles.thCenter}>Planned</th>
                <th style={styles.thCenter}>Completed</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => (
                <tr key={candidate.staffId}>
                  <td style={styles.td}>{candidate.staffName}</td>
                  <td style={styles.td}>{candidate.staffRole}</td>
                  <td style={styles.tdCenter}>{candidate.appraiseSuggested ? '\u2713' : ''}</td>
                  <td style={styles.tdCenter}>{candidate.planned ? '\u2713' : ''}</td>
                  <td style={styles.tdCenter}>{candidate.completed ? '\u2713' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSelfAssessment && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Self-Assessment</div>
          {appraisal.selfAssessmentItems && appraisal.selfAssessmentItems.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Development Need</th>
                  <th style={styles.th}>Appraisee Response</th>
                </tr>
              </thead>
              <tbody>
                {[...appraisal.selfAssessmentItems]
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((item, index) => (
                    <tr key={item.id ?? index}>
                      <td
                        style={styles.td}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.developmentNeed) }}
                      />
                      <td
                        style={styles.td}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.appraiseeResponse) }}
                      />
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '8px' }}>No self-assessment recorded</div>
          )}
          {appraisal.selfAssessmentAdditionalContext && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontWeight: 600, color: '#555', marginBottom: '4px' }}>Additional Context:</div>
              <div
                style={styles.feedbackContent}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(appraisal.selfAssessmentAdditionalContext) }}
              />
            </div>
          )}
        </div>
      )}

      {showSkillAreaStrengths && appraisal.skillAreaFeedback.length > 0 && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Strengths & Development Needs</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Skill Area</th>
                <th style={styles.thCenter}>Rating</th>
                <th style={styles.th}>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {[...appraisal.skillAreaFeedback]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((area) => (
                  <tr key={area.skillAreaId}>
                    <td style={styles.td}>{area.skillAreaName}</td>
                    <td style={styles.tdCenter}>{area.rating ? (RATING_LABELS[area.rating] || area.rating) : '-'}</td>
                    <td style={styles.td}>
                      {area.combinedText ? (
                        <div
                          style={styles.feedbackContent}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(area.combinedText) }}
                        />
                      ) : (
                        <span style={{ color: '#888', fontStyle: 'italic' }}>{'\u2014'}</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {showPerformanceSummary && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Performance Summary</div>
          {/* #1089: Performance Summary shows only the Final Rating (FSR ON); Proposed Rating lives in Notes to TC. */}
          {appraisal.finalRating ? (
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontWeight: 600, color: '#555' }}>Final Rating: </span>
              <span>{RATING_LABELS[appraisal.finalRating] || appraisal.finalRating}</span>
            </div>
          ) : null}
          {appraisal.performanceSummary ? (
            <div
              style={styles.feedbackContent}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(appraisal.performanceSummary) }}
            />
          ) : (
            <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '8px' }}>No performance summary recorded</div>
          )}
        </div>
      )}

      {showForwardLookingPlan && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Forward-Looking Plan</div>
          {appraisal.forwardLookingPlan && appraisal.forwardLookingPlan.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Development Need</th>
                  <th style={styles.th}>Action to Resolve</th>
                </tr>
              </thead>
              <tbody>
                {[...appraisal.forwardLookingPlan]
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((item, index) => (
                    <tr key={item.id ?? index}>
                      <td
                        style={styles.td}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.developmentNeed) }}
                      />
                      <td
                        style={styles.td}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.actionToResolve) }}
                      />
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '8px' }}>No forward-looking plan recorded</div>
          )}
        </div>
      )}
    </div>
  );
}
